/**
 * Privacy Settings Component
 * Allows users to control analytics tracking preferences
 */

import { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics';
import './PrivacySettings.css';

interface PrivacySettingsProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PrivacySettings({ isOpen, onClose }: PrivacySettingsProps) {
  const [analyticsEnabled, setAnalyticsEnabled] = useState(true);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState('');

  // Load current opt-out status on mount
  useEffect(() => {
    const optedOut = analyticsService.getOptOutStatus();
    setAnalyticsEnabled(!optedOut);
  }, [isOpen]);

  const handleToggle = () => {
    const newEnabledState = !analyticsEnabled;
    const optOut = !newEnabledState;

    // Update analytics service
    analyticsService.setOptOut(optOut);

    // Update local state
    setAnalyticsEnabled(newEnabledState);

    // Show confirmation message
    const message = newEnabledState
      ? '✓ Analytics enabled. We\'ll collect anonymous usage data to improve the app.'
      : '✓ Analytics disabled. No usage data will be collected.';
    
    setConfirmationMessage(message);
    setShowConfirmation(true);

    // Hide confirmation after 3 seconds
    setTimeout(() => {
      setShowConfirmation(false);
    }, 3000);
  };

  if (!isOpen) return null;

  return (
    <div className="privacy-settings-overlay" onClick={onClose}>
      <div className="privacy-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="privacy-settings-header">
          <h2>Privacy Settings</h2>
          <button
            className="privacy-settings-close"
            onClick={onClose}
            aria-label="Close privacy settings"
          >
            ×
          </button>
        </div>

        <div className="privacy-settings-content">
          <div className="privacy-section">
            <h3>Analytics & Usage Data</h3>
            <p className="privacy-description">
              We collect anonymous usage data to understand how you use 8-Bit Composer
              and improve the app. This helps us prioritize features and fix issues.
            </p>

            <div className="privacy-toggle-container">
              <label className="privacy-toggle-label">
                <span>Enable Analytics</span>
                <button
                  className={`privacy-toggle ${analyticsEnabled ? 'enabled' : 'disabled'}`}
                  onClick={handleToggle}
                  role="switch"
                  aria-checked={analyticsEnabled}
                  aria-label="Toggle analytics"
                >
                  <span className="privacy-toggle-slider"></span>
                </button>
              </label>
              <p className="privacy-toggle-status">
                {analyticsEnabled ? 'Analytics enabled' : 'Analytics disabled'}
              </p>
            </div>

            {showConfirmation && (
              <div className="privacy-confirmation" role="status" aria-live="polite">
                {confirmationMessage}
              </div>
            )}
          </div>

          <div className="privacy-section">
            <h3>What We Collect</h3>
            <ul className="privacy-list">
              <li>Which instruments and features you use</li>
              <li>How you interact with the sequencer grid</li>
              <li>Playback and composition patterns</li>
              <li>AI generation usage (when applicable)</li>
              <li>Session duration and engagement metrics</li>
            </ul>
          </div>

          <div className="privacy-section">
            <h3>What We Don't Collect</h3>
            <ul className="privacy-list">
              <li>IP addresses or precise location data</li>
              <li>Any data when analytics is disabled</li>
            </ul>
          </div>

          <div className="privacy-section privacy-note">
            <p>
              <strong>Note:</strong> Your browser's "Do Not Track" (DNT) setting is
              automatically respected. If DNT is enabled, analytics will be disabled
              regardless of this setting.
            </p>
          </div>
        </div>

        <div className="privacy-settings-footer">
          <button className="privacy-button-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
